/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import styles from './styles.module.scss';
import { Text, View } from '@az/starc-ui';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useLabels } from '@/hooks/useLabels';
import { useProductSpecifications, calculateGap, handleViewAllClick } from './utils';
import cx from 'classnames';

export const PdpSpecifications = () => {
  const { data: productDetailsData, status: productDetailsSuccess } = useProductDetails();
  const skuDetailsResult = useProductSkuDetails({
    skuIds:
      productDetailsSuccess === 'success' && productDetailsData?.product?.itemId
        ? [productDetailsData?.product.itemId]
        : null,
  });
  const skuDetails = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const labels = useLabels();
  const productSpecifications = useProductSpecifications({
    product: productDetailsData?.product,
    skuDetails: skuDetails,
    labels: labels as {
      lblWarranty: string;
      lblPartNumber: string;
      lblSkuNumber: string;
      lblWeight: string;
      label_DetailsPage_body_Location: string;
      label_DetailsPage_body_ApplicationSmall: string;
      label_CategoryPage_body_Notes: string;
      label_product_bulletins: string;
      label_safety_data_sheets: string;
    },
    limit: 4,
  });

  return productSpecifications?.length > 0 ? (
    <View direction="column">
      <View.Item>
        <View.Item>
          <View direction="row" justify="space-between">
            <View.Item>
              <Text variant="small-body" weight="bold" className={styles.starcSmallBodyOverride}>
                {`${labels.label_pdpSpecifications_h1}:`}
              </Text>
            </View.Item>
            <View.Item>
              <a
                href="#product-specifications"
                onClick={handleViewAllClick}
                aria-label="View all product specifications"
                className={styles.viewAllLink}
              >
                <Text
                  variant="small-body"
                  weight="bold"
                  decoration="underline"
                  className={cx(styles.starcSmallBodyOverride, styles.viewAllLabel)}
                >
                  {labels.label_view_all}
                </Text>
              </a>
            </View.Item>
          </View>
        </View.Item>
        <View.Item>
          <View direction="row" wrap padding={[2, 0, 0, 0]}>
            {productSpecifications?.map((specification, index) => {
              return (
                <View.Item
                  columns={6}
                  gapBefore={{
                    s: calculateGap('s', index),
                    m: calculateGap('m', index),
                    l: calculateGap('l', index),
                  }}
                  attributes={{
                    style: {
                      marginTop: index > 1 ? 8 : 0,
                      minWidth: 0,
                      alignSelf: 'stretch',
                    },
                  }}
                  key={specification.key + index}
                >
                  <View
                    direction="column"
                    align="center"
                    padding={[2, 4]}
                    className={styles.specCard}
                    attributes={{
                      style: {
                        backgroundColor: '#f8f8f8',
                        borderRadius: '4px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      },
                    }}
                  >
                    <View.Item attributes={{ style: { width: '100%' } }}>
                      <Text
                        variant="small-body"
                        weight="bold"
                        align="center"
                        className={styles.starcSmallBodyOverride}
                      >
                        {specification.label}:
                      </Text>
                    </View.Item>
                    <View.Item attributes={{ style: { width: '100%' } }}>
                      <Text
                        variant="small-body"
                        align="center"
                        className={styles.starcSmallBodyOverride}
                        attributes={{
                          style: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      >
                        {specification.value}
                      </Text>
                    </View.Item>
                  </View>
                </View.Item>
              );
            })}
          </View>
        </View.Item>
      </View.Item>
    </View>
  ) : null;
};
